import { useQuery, useMutation } from "@tanstack/react-query";
import {
  fetch2,
  fetch3,
  fetch4,
  fetch5,
  fetch6,
  fetch8,
} from "../commonApis/commonApis";
import { useDispatch } from "react-redux";
import { addClient, setClientList } from "../features/client/clientSlice";
const url = process.env.REACT_APP_BASE_URL;

type MerchantData = {
  name: string;
  mobileNo: string;
  email: string;
  contactType: string;
  vatNumber: string;
  nationalId: string;
  nationalCopy: string;
  country: string;
  hub: string;
  city: string;
  paymentTerm: string;
  access: string;
  location: string;
  status?: string;
  crCopy?: File | any;
  vatDocument?: File | any;
  companyLogo?: File | any;
  contractDocument?: File | any;
  merchantCountryId?: string;
  merchantCityId?: string;
  merchantDistrictId?: string;
  merchantShortAddress?: string;
  merchantFullAddress?: string;
  merchantLatitude?: string;
  merchantLongitude?: string;
  merchantPinCode?: string;
};

type LinkData = {
  id: string;
  clientId?: string;
};

type MerchantStatus = {
  status: string;
};

interface ApiResponse {
  success: boolean;
  data: any;
  message?: string;
  statusCode?: number;
  statusMessage?: string;
  error?: any;
}

export const useMerchantCreateMutation = () => {
  const dispatch = useDispatch();
  return useMutation({
    mutationFn: async (payload: MerchantData) => {
      const res: ApiResponse = await fetch2(`${url}/api/lm-client`, payload);

      // Manually throw error if success is false
      if (!res.success) {
        throw res.message || res?.error || "Something went wrong";
      }

      return res;
    },
    onSuccess: (data: any) => {
      console.log("✅ Merchant Created successfully:", data);
      // dispatch(addClient(data));
    },
    onError: (error: any) => {
      console.error("❌ Merchant create request failed:", error.message);
      return error;
    },
  });
};

export const useMerchantListData = (
  page: number,
  limit: number,
  searchFor: string,
) => {
  const dispatch = useDispatch();
  return useQuery({
    queryKey: ["clientList", page, limit, searchFor],
    queryFn: async () => {
      const res = await fetch3(
        `${url}/api/lm-client?page=${page}&limit=${limit}&searchFor=${searchFor}`,
        "get",
      );

      if (!res || !res.data) {
        return res.message;
      }

      dispatch(setClientList(res.data));
      return res.data;
    },
    staleTime: 1000 * 60 * 10, // Cache for 10 minutes
    gcTime: 1000 * 60 * 15, // Keep in memory for 15 minutes
  });
};

export const useMerchantAllListData = () => {
  return useQuery({
    queryKey: ["clientAllList"],
    queryFn: async () => {
      const res = await fetch3(`${url}/api/lm-client/all`, "get");

      if (!res || !res.data) {
        return res.message;
      }

      return res.data; // 👈 This returns your full user object
    },
    staleTime: 1000 * 60 * 5,
  });
};

// Fetch Merchant filtered by selected platform (third_party id / sales channel id)
export const useMerchantListByPlatform = (platformId?: string) => {
  return useQuery({
    queryKey: ["clientPlatformList", platformId],
    enabled: !!platformId,
    queryFn: async () => {
      if (!platformId) {
        console.log("⚠️ No platformId provided to useMerchantListByPlatform");
        return [];
      }
      console.log("🔍 Fetching Merchant for platform:", platformId);
      const res = await fetch3(
        `${url}/api/lm-client/by-platform/${platformId}`,
        "get",
      );
      console.log("📦 Platform Merchants response:", res);
      if (!res || !res.data) {
        console.log("⚠️ No data in platform Merchants response");
        return [];
      }
      console.log("✅ Platform Merchants data:", res.data);
      return res.data;
    },
    staleTime: 1000 * 60 * 5,
  });
};

export const useAllFMMerchantData = () => {
  return useQuery({
    queryKey: ["allFMMerchantData"],
    queryFn: async () => {
      const res = await fetch3(`${url}/api/lm-client/merchant/all`, "get");

      if (!res || !res.data) {
        return res.message;
      }

      return res.data;
    },
    staleTime: 1000 * 60 * 5,
  });
};

export const useFetchMerchantSingleData = (id: string) => {
  return useQuery({
    queryKey: ["singleClientData"],
    queryFn: async () => {
      const res = await fetch3(
        `${url}/api/lm-client/get-single-client/${id}`,
        "get",
      );

      if (!res || !res.data) {
        return res.message;
      }

      return res.data; // 👈 This returns your full user object
    },
    staleTime: 1000 * 60 * 5,
  });
};

export const useMerchantUpdateMutation = (id: string) => {
  return useMutation({
    mutationFn: async (payload: MerchantData) => {
      const res: ApiResponse = await fetch6(
        `${url}/api/lm-client/${id}`,
        payload,
      );

      // Manually throw error if success is false
      if (!res.success) {
        throw res.message || res?.error || "Something went wrong";
      }

      return res;
    },
    onSuccess: (data: any) => {
      console.log("✅ Merchant Created successfully:", data);
    },
    onError: (error: any) => {
      console.error("❌ Merchant create request failed:", error.message);
      return error;
    },
  });
};

export const useMerchantStatusMutation = (id: string) => {
  return useMutation({
    mutationFn: async (payload: MerchantStatus) => {
      const res: ApiResponse = await fetch5(
        `${url}/api/lm-client/status/${id}`,
        "PATCH",
        payload,
      );

      // Manually throw error if success is false
      if (!res.success) {
        throw res.message || "Something went wrong";
      }

      return res.data;
    },
    onSuccess: (data: any) => {
      console.log("✅ Merchant Created successfully:", data);
    },
    onError: (error: any) => {
      console.error("❌ Merchant create request failed:", error.message);
    },
  });
};

export const useMerchantDeleteMutation = (id: string) => {
  return useMutation({
    mutationFn: async () => {
      const res: ApiResponse = await fetch5(
        `${url}/api/lm-client/delete-client/${id}`,
        "DELETE",
      );

      // Manually throw error if success is false
      if (!res.success) {
        throw res.message || "Something went wrong";
      }

      return res.data;
    },
    onSuccess: (data: any) => {
      console.log("✅ Merchant Created successfully:", data);
    },
    onError: (error: any) => {
      console.error("❌ Merchant create request failed:", error.message);
    },
  });
};

export const useCustomerListData = (
  page: number,
  limit: number,
  searchFor: string
) => {
  return useQuery({
    queryKey: ["customerList", page, limit, searchFor],
    queryFn: async () => {
      const res = await fetch3(
        `${url}/api/customers?page=${page}&limit=${limit}&search=${searchFor}`,
        "get"
      );

      if (!res || !res.data) {
        throw new Error(res?.message || "Failed to fetch customers");
      }

      return res.data; 
    },
    staleTime: 1000 * 60 * 5,
  });
};

export const useCustomerDetails = (id: string | undefined) => {
  return useQuery({
    queryKey: ["customer-details", id],
    enabled: !!id, 
    queryFn: async () => {
      const res = await fetch3(`${url}/api/customers/${id}`, "get");

      if (!res || !res.data) {
        throw new Error(res?.message || "Failed to fetch customer details");
      }

      return res.data; 
    },
    staleTime: 1000 * 60 * 5,
  });
};

export const useMerchantRequestListData = (
  page: number,
  limit: number,
  searchFor: string,
) => {
  return useQuery({
    queryKey: ["clientRequestList"],
    queryFn: async () => {
      const res = await fetch3(
        `${url}/api/salla?page=${page}&limit=${limit}&searchFor=${searchFor}`,
        "get",
      );

      if (!res || !res.data) {
        return res.message;
      }

      return res.data; // 👈 This returns your full user object
    },
    staleTime: 1000 * 60 * 5,
  });
};

export const useMerchantRequestAcceptMutation = () => {
  return useMutation({
    mutationFn: async (payload: MerchantData) => {
      const res: ApiResponse = await fetch5(
        `${url}/api/salla`,
        "PATCH",
        payload,
      );

      // Manually throw error if success is false
      if (!res.success) {
        throw res.message || "Something went wrong";
      }

      return res.data;
    },
    onSuccess: (data: any) => {
      console.log("✅ Merchant Created successfully:", data);
    },
    onError: (error: any) => {
      console.error("❌ Merchant create request failed:", error.message);
    },
  });
};

export const useMerchantAcceptLinkMutation = () => {
  return useMutation({
    mutationFn: async (payload: LinkData) => {
      const res: ApiResponse = await fetch5(
        `${url}/api/salla/accept-link`,
        "PATCH",
        payload,
      );

      // Manually throw error if success is false
      if (!res.success) {
        throw res.message || "Something went wrong";
      }

      return res.data;
    },
    onSuccess: (data: any) => {
      console.log("✅ Merchant Created successfully:", data);
    },
    onError: (error: any) => {
      console.error("❌ Merchant create request failed:", error.message);
    },
  });
};

export const useMerchantBulkDocMutation = () => {
  return useMutation({
    mutationFn: async (payload: any) => {
      const res: ApiResponse = await fetch8(
        `${url}/api/bulk/bulk-lmClient`,
        "POST",
        payload,
      );

      if (!res.success) {
        throw res.message || "Something went wrong";
      }

      return res;
    },
    onSuccess: (res: any) => {
      console.log("Merchant Bulk Created successfully:", res);
      return res;
    },
    onError: (error: any) => {
      console.error("Merchant Bulk create request failed:", error.message);
    },
  });
};

export const useFetchMerchantTemplate = () => {
  return useQuery({
    queryKey: ["templates"],
    queryFn: async () => {
      const res = await fetch3(`${url}/api/templates/lm-client`, "get");
      if (!res || !res.data) return res.message;
      return res.data;
    },
    staleTime: 1000 * 60 * 5,
  });
};

// Client list Weight wise start
export const useMerchantWeightAllListData = () => {
  return useQuery({
    queryKey: ["clientAllWeightList"],
    queryFn: async () => {
      const res = await fetch3(`${url}/api/lm-client/has-weight`, "get");

      if (!res || !res.data) {
        return res.message;
      }

      return res.data; // 👈 This returns your full user object
    },
    staleTime: 1000 * 60 * 5,
  });
};

// Client access center list
export const useMerchantAccessCenterListData = () => {
  return useQuery({
    queryKey: ["clientAccessCenterList"],
    queryFn: async () => {
      const res = await fetch3(`${url}/api/lm-client-access-center`, "GET");

      if (!res || !res.data) {
        return res.message;
      }

      return res.data;
    },
    staleTime: 1000 * 60 * 5,
  });
};
// LM Client permissions fetch by clientId
export const useMerchantAccessByIdData = (clientId: string) => {
  return useQuery({
    queryKey: ["clientAccessById", clientId],
    queryFn: async () => {
      const res = await fetch3(
        `${url}/api/lm-client-access-center/${clientId}`,
        "GET",
      );

      if (!res || !res.data) {
        return res.message;
      }

      return res.data;
    },
    staleTime: 1000 * 60 * 5,
  });
};
// LM Client permissions update
export const useMerchantAccessCreateMutation = (clientId: string) => {
  return useMutation({
    mutationFn: async (payload: any) => {
      const res = await fetch4(
        `${url}/api/lm-client-access-center/${clientId}`,
        payload,
      );

      if (!res.success) {
        throw res.message || res?.error || "Something went wrong";
      }

      return res.data;
    },
  });
};
